// src/features/dashboard/components/AssignmentEfficiencySection.tsx

import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  AccessTime,
  TrendingUp,
  Error,
  Schedule,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { AssignmentEfficiencyResponse } from '../types';

interface AssignmentEfficiencySectionProps {
  data: AssignmentEfficiencyResponse;
}

interface SecondaryMetricCardProps {
  value: string | number;
  label: string;
  resourceKey: string;
  icon: React.ReactNode;
  color: string;
}

const SecondaryMetricCard: React.FC<SecondaryMetricCardProps> = ({
  value,
  label,
  resourceKey,
  icon,
  color
}) => {
  const translate = useTranslate();

  return (
    <Card sx={{
      height: '100px',
      backgroundColor: '#f8f9fa',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#f0f1f3',
      }
    }}>
      <CardContent sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: '100%',
        '&:last-child': { pb: 2 }
      }}>
        <Box sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {React.cloneElement(icon as React.ReactElement, { 
            sx: { color, fontSize: 20 } 
          })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {translate(resourceKey)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const AssignmentEfficiencySection: React.FC<AssignmentEfficiencySectionProps> = ({ data }) => {
  const translate = useTranslate();

  // Default values if data is not available
  const peakDemandTimes = data.peakDemandTimes || ['10:00-12:00', '14:00-16:00'];
  const conversionRate = data.pendingToActiveConversion || 0;
  const avgTimeToFirstUse = data.averageTimeToFirstUse || 0;
  const neverUsedAssignments = data.neverUsedAssignments || 0;
  const autoArchivedExpired = data.autoArchivedExpired || 0;

  return (
    <Card sx={{
      mb: 3,
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            {translate('resources.dashboard.assignment_efficiency')}
          </Typography>
        </Box>

        {/* Peak Demand Times */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AccessTime sx={{ fontSize: 18, color: '#666' }} />
            <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', color: '#666' }}>
              {translate('resources.dashboard.peak_demand_times')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {peakDemandTimes.map((time, idx) => (
              <Chip
                key={idx}
                label={time}
                size="small"
                sx={{
                  borderColor: '#1fa2bf',
                  color: '#1fa2bf',
                  backgroundColor: 'rgba(31, 162, 191, 0.05)',
                  '& .MuiChip-label': {
                    fontSize: '0.75rem',
                    px: 1.5
                  }
                }}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        {/* Secondary Metrics Grid */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <SecondaryMetricCard
              value={`${conversionRate.toFixed(1)}%`}
              label="Conversion Rate"
              resourceKey="resources.dashboard.conversion_rate"
              icon={<TrendingUp />}
              color="#4caf50"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <SecondaryMetricCard
              value={`${avgTimeToFirstUse.toFixed(1)}h`}
              label="Avg Time to First Use"
              resourceKey="resources.dashboard.avg_time_first_use"
              icon={<AccessTime />}
              color="#2196f3"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <SecondaryMetricCard
              value={neverUsedAssignments}
              label="Never Used"
              resourceKey="resources.dashboard.never_used_assignments"
              icon={<Error />}
              color="#ff9800"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <SecondaryMetricCard
              value={autoArchivedExpired}
              label="Auto Archived"
              resourceKey="resources.dashboard.auto_archived_expired"
              icon={<Schedule />}
              color="#9c27b0"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default AssignmentEfficiencySection;